'use client';
import React, { useState } from 'react';
import { X, UserPlus, Loader2, CheckCircle } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface AddAthleteModalProps {
  coachId: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface AthleteFormData {
  firstName: string;
  lastName: string;
  email: string;
  sport: string;
  position: string;
  gradYear: string;
  phone: string;
  profileStatus: 'pending' | 'active';
}

const SPORTS = [
  'Baseball', 'Basketball', 'Football', 'Soccer', 'Softball',
  'Track & Field', 'Volleyball', 'Wrestling', 'Lacrosse', 'Swimming',
  'Tennis', 'Golf', 'Cross Country', 'Hockey', 'Other',
];

const CURRENT_YEAR = new Date().getFullYear();
const GRAD_YEARS = Array.from({ length: 8 }, (_, i) => String(CURRENT_YEAR + i));

export default function AddAthleteModal({ coachId, onClose, onSuccess }: AddAthleteModalProps) {
  const [form, setForm] = useState<AthleteFormData>({
    firstName: '',
    lastName: '',
    email: '',
    sport: '',
    position: '',
    gradYear: '',
    phone: '',
    profileStatus: 'pending',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (field: keyof AthleteFormData, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const validate = (): string => {
    if (!form.firstName.trim()) return 'First name is required.';
    if (!form.lastName.trim()) return 'Last name is required.';
    if (!form.email.trim()) return 'Email is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return 'Enter a valid email address.';
    if (!form.sport) return 'Sport is required.';
    if (!form.position.trim()) return 'Position is required.';
    if (!form.gradYear) return 'Graduation year is required.';
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const supabase = createClient();
      const fullName = `${form.firstName.trim()} ${form.lastName.trim()}`;
      const initials = `${form.firstName.trim()[0] ?? ''}${form.lastName.trim()[0] ?? ''}`.toUpperCase();

      // 1. Create athlete profile
      const { data: athleteProfile, error: profileError } = await supabase
        .from('athlete_profiles')
        .insert({
          name: fullName,
          email: form.email.trim().toLowerCase(),
          sport: form.sport,
          position: form.position.trim(),
          grad_year: parseInt(form.gradYear, 10),
          avatar_initials: initials,
          profile_status: form.profileStatus,
          created_by_coach_id: coachId,
        })
        .select('id')
        .single();

      if (profileError) {
        throw new Error(profileError.message);
      }

      if (!athleteProfile?.id) {
        throw new Error('Failed to create athlete profile.');
      }

      // 2. Link athlete to coach in coach_athletes
      const { error: linkError } = await supabase
        .from('coach_athletes')
        .insert({
          coach_id: coachId,
          athlete_id: athleteProfile.id,
          status: 'active',
          onboarding_status: form.profileStatus === 'pending' ? 'pending' : 'complete',
          invited_email: form.email.trim().toLowerCase(),
        });

      if (linkError) {
        // If linking fails, attempt to clean up the profile
        await supabase.from('athlete_profiles').delete().eq('id', athleteProfile.id);
        throw new Error(linkError.message);
      }

      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1200);
    } catch (err: any) {
      setError(err?.message ?? 'An unexpected error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-lg rounded-2xl overflow-hidden flex flex-col"
        style={{
          background: 'rgba(18,18,18,0.98)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
          maxHeight: '90vh',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 flex-shrink-0"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: 'rgba(225,6,0,0.12)', border: '1px solid rgba(225,6,0,0.25)' }}
            >
              <UserPlus size={15} style={{ color: '#E10600' }} />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-zinc-100">Add Athlete</h2>
              <p className="text-xs text-zinc-500">Create a new athlete profile</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-zinc-500 hover:text-zinc-200 transition-colors"
            style={{ background: 'transparent' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            <X size={16} />
          </button>
        </div>

        {/* Success state */}
        {success ? (
          <div className="flex-1 flex flex-col items-center justify-center py-12 px-6">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
              style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)' }}
            >
              <CheckCircle size={28} className="text-emerald-400" />
            </div>
            <h3 className="text-base font-semibold text-zinc-100 mb-1">Athlete Added!</h3>
            <p className="text-sm text-zinc-500 text-center">
              {form.firstName} {form.lastName} has been added to your roster.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
            <div className="px-6 py-5 space-y-4">
              {/* Name row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-zinc-400 mb-1.5">
                    First Name <span style={{ color: '#E10600' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={e => handleChange('firstName', e.target.value)}
                    placeholder="e.g. Marcus"
                    className="input-field w-full"
                    disabled={submitting}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-400 mb-1.5">
                    Last Name <span style={{ color: '#E10600' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={e => handleChange('lastName', e.target.value)}
                    placeholder="e.g. Webb"
                    className="input-field w-full"
                    disabled={submitting}
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1.5">
                  Email <span style={{ color: '#E10600' }}>*</span>
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => handleChange('email', e.target.value)}
                  placeholder="athlete@example.com"
                  className="input-field w-full"
                  disabled={submitting}
                />
              </div>

              {/* Sport + Position */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-zinc-400 mb-1.5">
                    Sport <span style={{ color: '#E10600' }}>*</span>
                  </label>
                  <select
                    value={form.sport}
                    onChange={e => handleChange('sport', e.target.value)}
                    className="input-field w-full"
                    disabled={submitting}
                  >
                    <option value="">Select sport</option>
                    {SPORTS.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-400 mb-1.5">
                    Position <span style={{ color: '#E10600' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={form.position}
                    onChange={e => handleChange('position', e.target.value)}
                    placeholder="e.g. Shortstop"
                    className="input-field w-full"
                    disabled={submitting}
                  />
                </div>
              </div>

              {/* Grad Year + Phone */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-zinc-400 mb-1.5">
                    Grad Year <span style={{ color: '#E10600' }}>*</span>
                  </label>
                  <select
                    value={form.gradYear}
                    onChange={e => handleChange('gradYear', e.target.value)}
                    className="input-field w-full"
                    disabled={submitting}
                  >
                    <option value="">Select year</option>
                    {GRAD_YEARS.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-400 mb-1.5">
                    Phone <span className="text-zinc-600">(optional)</span>
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={e => handleChange('phone', e.target.value)}
                    placeholder="e.g. 555-123-4567"
                    className="input-field w-full"
                    disabled={submitting}
                  />
                </div>
              </div>

              {/* Profile Status */}
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-2">
                  Profile Status
                </label>
                <div className="flex gap-3">
                  {(['pending', 'active'] as const).map(status => (
                    <button
                      key={status}
                      type="button"
                      onClick={() => handleChange('profileStatus', status)}
                      disabled={submitting}
                      className="flex-1 py-2 rounded-lg text-xs font-medium transition-all duration-150 capitalize"
                      style={form.profileStatus === status ? {
                        background: 'rgba(225,6,0,0.12)',
                        border: '1px solid rgba(225,6,0,0.35)',
                        color: '#FF3B30',
                      } : {
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        color: '#71717a',
                      }}
                    >
                      {status}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-zinc-600 mt-1.5">
                  {form.profileStatus === 'pending' ?'Athlete profile created but not yet onboarded.' :'Athlete is active and can log in.'}
                </p>
              </div>

              {/* Error */}
              {error && (
                <div
                  className="rounded-lg px-4 py-3 text-sm"
                  style={{ background: 'rgba(139,0,0,0.15)', border: '1px solid rgba(225,6,0,0.3)', color: '#FF3B30' }}
                >
                  {error}
                </div>
              )}
            </div>

            {/* Footer */}
            <div
              className="flex items-center justify-end gap-3 px-6 py-4 flex-shrink-0"
              style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
            >
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 rounded-lg text-sm font-medium text-zinc-400 transition-all duration-150"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                onMouseEnter={e => (e.currentTarget.style.color = '#ffffff')}
                onMouseLeave={e => (e.currentTarget.style.color = '#a1a1aa')}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-5 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all duration-200"
                style={{
                  background: submitting ? 'rgba(225,6,0,0.4)' : 'linear-gradient(135deg, #E10600, #8B0000)',
                  color: '#ffffff',
                  boxShadow: submitting ? 'none' : '0 0 16px rgba(225,6,0,0.3)',
                  opacity: submitting ? 0.7 : 1,
                }}
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <UserPlus size={14} />
                    Add Athlete
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
